import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { TranslationEntry } from '@adornis/translation/db/translation-entry.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { TranslationDictionaryMentoring } from '../../translations.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { acss } from '@adornis/chemistry/directives/acss.js';

@customElement('mentoring-translation-editor')
export class MentoringTranslationEditor extends ChemistryLitElement {
  translator = new TranslationController<TranslationDictionaryMentoring>(this);

  override render() {
    const { dataViewOptions, filterOptions } = buildFullOptions({
      class: TranslationEntry,
      operation: 'subscribeMentoringTranslations',
      isColumnSelectable: true,
      view: new View({
        filter: new AdornisFilter({
          searchFields: ['keyPath', 'translation'],
        }),
      }),
      customColumns: [
        { fieldName: 'keyPath', selectionSet: { _id: 1, keyPath: 1 } },
        { fieldName: 'language', selectionSet: { _id: 1, language: 1 } },
        {
          fieldName: 'translation',
          renderItem: instance => html` <editable-text
            ${css({
              border: instance.overrideByUser ? `1px solid ${this.colors.tone.error}` : '1px solid transparent',
              borderRadius: this.sizes.borderRadius,
            })}
            .text=${instance.translation}
            @value-picked=${(e: ValueEvent<string>) =>
              XDialog.guard(() =>
                TranslationEntry.setTranslation(instance.language, instance.keyPath, e.detail.value)(),
              )}
          >
          </editable-text>`,
          selectionSet: { translation: 1, language: 1, keyPath: 1, overrideByUser: 1, _id: 1 },
        },
      ],
      selectionSet: {
        _id: 1,
      },
    });

    return html`
      <x-flex space="lg">
        <bh-breadcrumb .goUp=${() => goTo('/')}></bh-breadcrumb>
        <x-h1> ${this.translator.translate('route', 'translation-editor')} </x-h1>
        <x-filter-editor .options=${filterOptions}></x-filter-editor>
        <x-data-view .options=${dataViewOptions}></x-data-view>
      </x-flex>
    `;
  }
}

/**
 * @element editable-x-text
 */
@customElement('editable-text')
export class EditableXText extends ChemistryLitElement {
  @state() text = '';

  override render() {
    return html`
      <x-flex
        ${acss({
          position: 'relative',
          cursor: 'pointer',
          borderRadius: this.sizes.borderRadius,
        })}
      >
        <x-flex horizontal center crossaxis-center space="md">
          <x-input
            .value=${this.text}
            @value-changed=${(e: ValueEvent<string>) => (this.text = e.detail.value)}
          ></x-input
          ><x-button
            mode="icon"
            icon="save"
            @click=${() => {
              this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: this.text } }));
            }}
          ></x-button>
        </x-flex>
      </x-flex>
    `;
  }
}
